import React, { useState, useEffect, useRef } from 'react';
import { api, getImageUrl, pluralize } from '../api';
import { useCardImages } from '../utils/imageQueue';
import PhotoLightbox from './PhotoLightbox';
import { SectionLoader } from './NavIcons';
import { todayShop, useShopTimezone } from '../utils/timezone';

function SubscriptionCatalog({ onAddToCart, subscriptionCart, onSelectionChange }) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(subscriptionCart?.catalog_item || null);
  const [deliveryCount, setDeliveryCount] = useState(subscriptionCart?.delivery_count || 2);
  const shopTz = useShopTimezone();
  useEffect(() => {
    loadCatalog();
  }, []);

  useEffect(() => {
    if (subscriptionCart?.catalog_item) {
      setSelectedItem(subscriptionCart.catalog_item);
      setDeliveryCount(subscriptionCart.delivery_count || 2);
    } else {
      setSelectedItem(null);
      setDeliveryCount(2);
    }
    // Следим за id (примитив), в теле присваиваем весь объект catalog_item
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionCart?.catalog_item?.id, subscriptionCart?.delivery_count]);

  const loadCatalog = async () => {
    try {
      const data = await api.getSubscriptionCatalog();
      setItems(data);
    } catch (e) {
      console.error('Failed to load subscription catalog:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (item) => {
    if (selectedItem?.id === item.id) {
      setSelectedItem(null);
      setDeliveryCount(2);
      if (onSelectionChange) onSelectionChange(null, 0);
    } else {
      setSelectedItem(item);
      setDeliveryCount(2);
      if (onSelectionChange) onSelectionChange(item, 2);
    }
  };

  // Макс. доставок = дни в окне 2 месяцев (от завтра до today+2мес) — в поясе магазина;
  // пока пояс не пришёл из настроек — показываем загрузку (ниже)
  let maxDeliveries = 2;
  if (shopTz) {
    const today = todayShop(shopTz);
    const minDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
    const maxDate = new Date(today.getFullYear(), today.getMonth() + 2, today.getDate());
    maxDeliveries = Math.max(2, Math.floor((maxDate - minDate) / (1000 * 60 * 60 * 24)) + 1);
  }

  const handleIncrement = () => {
    const nc = Math.min(maxDeliveries, deliveryCount + 1);
    setDeliveryCount(nc);
    if (onSelectionChange) onSelectionChange(selectedItem, nc);
  };

  const handleDecrement = () => {
    if (deliveryCount <= 2) {
      setSelectedItem(null);
      setDeliveryCount(2);
      if (onSelectionChange) onSelectionChange(null, 0);
    } else {
      const nc = deliveryCount - 1;
      setDeliveryCount(nc);
      if (onSelectionChange) onSelectionChange(selectedItem, nc);
    }
  };



  if (isLoading || !shopTz) {
    return <SectionLoader />;
  }

  if (items.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>💐</div>
        <div>Подписки скоро появятся</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 80px' }}>
      <div className="products-container">
      <div style={{ padding: '16px 8px 16px', color: '#666', fontSize: 13, lineHeight: 1.5 }}>
        Регулярно обновляем цветы у вас дома. Вы выбираете размер и количество доставок, мы подбираем сезонные стебли и привозим их вместе с новой вазой. Чем больше доставок, тем выгоднее стоимость каждой.
      </div>
      <div className="products-grid">
        {items.map(item => (
          <SubscriptionCard
            key={item.id}
            item={item}
            isSelected={selectedItem?.id === item.id}
            deliveryCount={selectedItem?.id === item.id ? deliveryCount : 0}
            maxDeliveries={maxDeliveries}
            onSelect={() => handleSelect(item)}
            onIncrement={handleIncrement}
            onDecrement={handleDecrement}
            onGoToCart={() => onAddToCart(item, selectedItem?.id === item.id ? deliveryCount : 2)}
          />
        ))}
      </div>
      </div>

      {/* Кнопка "Перейти в корзину" рендерится в App.jsx */}
    </div>
  );
}

function SubscriptionCard({ item, isSelected, deliveryCount, maxDeliveries, onSelect, onIncrement, onDecrement, onGoToCart }) {
  const [currentImage, setCurrentImage] = useState(0);
  const images = item.images && item.images.length > 0
    ? item.images.map(img => ({
        small: img.image_small || img.thumb_url || img.image_url,
        medium: img.image_medium || img.image_url,
        large: img.image_large || img.image_url,
        default: img.image_medium || img.image_url,
      }))
    : [];
  const hasImages = images.length > 0;
  // Фото в карточке — средний размер через очередь (utils/imageQueue.js), большое — в зуме
  const imageBoxRef = useRef(null);
  const cardUrls = images.map(img => getImageUrl(img.medium));
  const imageReady = useCardImages(cardUrls, imageBoxRef, currentImage);
  const totalImages = images.length || 1;

  // Как на карточках товаров: свайп в сторону листает, тап-зоны 25/50/25 —
  // края переключают фото, центр открывает quick-view с описанием.
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const touchStartRef = useRef(null);
  const prevImage = () => setCurrentImage(prev => (prev - 1 + totalImages) % totalImages);
  const nextImage = () => setCurrentImage(prev => (prev + 1) % totalImages);

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const lastTouchEndRef = useRef(0);

  const handleTouchEnd = (e) => {
    lastTouchEndRef.current = Date.now();
    if (!touchStartRef.current) return;
    const touch = e.changedTouches[0];
    const dx = touch.clientX - touchStartRef.current.x;
    const dy = touch.clientY - touchStartRef.current.y;
    touchStartRef.current = null;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) nextImage(); else prevImage();
    } else if (Math.abs(dx) < 10 && Math.abs(dy) < 10 && hasImages) {
      const rect = e.currentTarget.getBoundingClientRect();
      const tapX = touch.clientX - rect.left;
      if (totalImages > 1 && tapX < rect.width * 0.25) prevImage();
      else if (totalImages > 1 && tapX > rect.width * 0.75) nextImage();
      else setLightboxOpen(true);
    }
  };

  const handleClick = (e) => {
    // Недавний тач уже обработан в handleTouchEnd — глушим синтезированный click,
    // но мышь/трекпад на тач-устройстве работает
    if (Date.now() - lastTouchEndRef.current < 700) return;
    if (!hasImages) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    if (totalImages > 1 && clickX < rect.width * 0.25) prevImage();
    else if (totalImages > 1 && clickX > rect.width * 0.75) nextImage();
    else setLightboxOpen(true);
  };

  // Цена: показываем цену за deliveryCount доставок если выбрано, иначе за 1
  const displayPrice = isSelected ? item.price * deliveryCount : item.price;

  return (
    <div className="product-card">
      <div
        className="product-image-container"
        ref={imageBoxRef}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onClick={handleClick}
      >
        <div className="product-image-placeholder">
          {hasImages ? (
            <>
              {images.map((img, index) => imageReady[index] && (
                <img
                  key={index}
                  src={cardUrls[index]}
                  alt={item.name}
                  decoding="async"
                  onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                  style={{
                    position: index === 0 ? 'relative' : 'absolute',
                    top: 0, left: 0, width: '100%', height: '100%',
                    objectFit: 'cover',
                    opacity: index === currentImage ? 1 : 0,
                    transition: 'opacity 0.25s ease',
                    pointerEvents: 'none',
                  }}
                />
              ))}
            </>
          ) : (
            '💐'
          )}
        </div>

        {totalImages > 1 && (
          <>
            <span className="image-nav image-nav-prev" aria-hidden="true">‹</span>
            <span className="image-nav image-nav-next" aria-hidden="true">›</span>
          </>
        )}

        {totalImages > 1 && (
          <div className="image-dots">
            {images.map((_, index) => (
              <span
                key={index}
                className={`image-dot ${index === currentImage ? 'active' : ''}`}
                onClick={(e) => { e.stopPropagation(); setCurrentImage(index); }}
              />
            ))}
          </div>
        )}

        {!isSelected && (
          <button
            className="add-to-cart-btn"
            onClick={(e) => { e.stopPropagation(); onSelect(); }}
            onTouchEnd={(e) => { e.stopPropagation(); }}
            aria-label="Выбрать подписку"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <path d="M16 10a4 4 0 0 1-8 0"></path>
            </svg>
          </button>
        )}
      </div>
      {lightboxOpen && hasImages && (
        <PhotoLightbox
          images={images.map(img => getImageUrl(img.large || img.default))}
          previews={cardUrls}
          startIndex={currentImage}
          alt={item.name}
          onClose={() => setLightboxOpen(false)}
          onIndexChange={setCurrentImage}
          panel={
            <>
              <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>{item.name}</div>
              {item.description && (
                <div style={{ marginBottom: 10, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  {item.description}
                </div>
              )}
              <div style={{ marginBottom: 10 }}>
                <span style={{ fontSize: 20, fontWeight: 800 }}>
                  {((item.price || 0) * (isSelected && deliveryCount > 0 ? deliveryCount : 1)).toLocaleString('ru-RU')}₽
                </span>
                <span className="product-step-badge" style={{ marginLeft: 8 }}>
                  {isSelected && deliveryCount > 0
                    ? `за ${deliveryCount} ${pluralize(deliveryCount, 'доставку', 'доставки', 'доставок')}`
                    : 'за 1 доставку'}
                </span>
              </div>
              {/* Тот же сценарий, что у заказа: одна строка действий постоянной
                  высоты — «Выбрать» → «− N + / Перейти в корзину» */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {isSelected ? (
                  <>
                    <button className="primary-btn primary-btn--cart" style={{ padding: '8px 16px' }} onClick={onDecrement}>−</button>
                    <span style={{ fontWeight: 700, minWidth: 24, textAlign: 'center' }}>{deliveryCount}</span>
                    <button className="primary-btn primary-btn--cart" style={{ padding: '8px 16px', opacity: deliveryCount >= maxDeliveries ? 0.4 : 1 }}
                      onClick={onIncrement} disabled={deliveryCount >= maxDeliveries}>+</button>
                    <button
                      className="primary-btn primary-btn--cart"
                      style={{ flex: 1, padding: '12px 16px' }}
                      onClick={() => {
                        setLightboxOpen(false);
                        onGoToCart(); // кладёт подписку в корзину и открывает её
                      }}
                    >
                      Перейти в корзину
                    </button>
                  </>
                ) : (
                  <button className="primary-btn" style={{ width: '100%', padding: '12px 16px' }} onClick={onSelect}>
                    В корзину
                  </button>
                )}
              </div>
            </>
          }
        />
      )}
      <div className="product-info" style={{ position: 'relative' }}>
        <h3 className="product-name">{item.name}</h3>
        {item.description && (
          <div style={{ fontSize: 12, color: '#888', marginBottom: 8, lineHeight: 1.3, paddingRight: isSelected ? 40 : 0 }}>
            {item.description}
          </div>
        )}
        <div className="product-price-row">
          <div className="product-price-info">
            <span className="product-price">
              {displayPrice.toLocaleString('ru-RU')} <span>₽</span>
            </span>
          </div>
        </div>
        {isSelected && (
          <div className="quantity-controls" style={{ position: 'absolute', bottom: 8, right: 8, flexDirection: 'column', gap: 0, alignItems: 'center' }}>
            <span
              className={`quantity-btn`}
              onClick={onIncrement}
              style={{ opacity: deliveryCount >= maxDeliveries ? 0.4 : 1, fontSize: 20, padding: '2px 8px' }}
            >
              +
            </span>
            <span className="quantity" style={{ fontSize: 17 }}>{deliveryCount}</span>
            <span className="quantity-btn" onClick={onDecrement} style={{ fontSize: 20, padding: '2px 8px' }}>−</span>
            <span style={{ fontSize: 10, color: '#888' }}>дост.</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default SubscriptionCatalog;
